import { useCallback, useEffect, useRef, useState } from 'react'

interface UseRectObserverResult<T> {
  elementRef: T | null
  setElementRef: (e: (T & HTMLElement) | null) => void
  elementWidth: number
  elementHeight: number
  ping: () => void
}

export const useRectObserver = <T>(): UseRectObserverResult<T> => {
  const [elementWidth, setElementWidth] = useState(0)
  const [elementHeight, setElementHeight] = useState(0)
  const [elementRef, setElementRef] = useState<(T & HTMLElement) | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const updateDimensions = useCallback(() => {
    if (elementRef) {
      setElementHeight(elementRef.clientHeight ?? 0)
      setElementWidth(elementRef.clientWidth ?? 0)
    }
  }, [elementRef])

  useEffect(() => {
    if (!elementRef) {
      return
    }
    resizeObserverRef.current = new ResizeObserver(() => {
      updateDimensions()
    })

    resizeObserverRef.current.observe(elementRef)

    updateDimensions()

    return () => {
      resizeObserverRef.current?.disconnect()
    }
  }, [elementRef, updateDimensions])

  useEffect(() => {
    window.addEventListener('resize', updateDimensions)
    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
  }, [updateDimensions])

  const handlePing = useCallback(() => {
    updateDimensions()
  }, [updateDimensions])

  return {
    elementRef,
    setElementRef,
    elementWidth,
    elementHeight,
    ping: handlePing
  }
}
